/**
 * SEO utility functions for managing dynamic meta tags
 */

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: object;
  breadcrumbs?: Array<{ name: string; url: string }>;
}

/**
 * Update SEO meta tags dynamically
 */
export function updateSEO(data: SEOData): void {
  // Update title
  if (data.title) {
    document.title = data.title;
    updateMetaTag('property', 'og:title', data.ogTitle || data.title);
    updateMetaTag('name', 'twitter:title', data.twitterTitle || data.title);
  }

  // Update description
  if (data.description) {
    updateMetaTag('name', 'description', data.description);
    updateMetaTag('property', 'og:description', data.ogDescription || data.description);
    updateMetaTag('name', 'twitter:description', data.twitterDescription || data.description);
  }

  // Update keywords
  if (data.keywords) {
    updateMetaTag('name', 'keywords', data.keywords);
  }

  // Update canonical URL
  if (data.canonical) {
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = data.canonical;
  }

  // Update Open Graph URL
  if (data.ogUrl) {
    updateMetaTag('property', 'og:url', data.ogUrl);
    updateMetaTag('name', 'twitter:url', data.ogUrl);
  }

  // Update Open Graph image
  if (data.ogImage) {
    updateMetaTag('property', 'og:image', data.ogImage);
    updateMetaTag('name', 'twitter:image', data.ogImage);
  }

  // Update structured data
  if (data.structuredData) {
    updateStructuredData(data.structuredData);
  }

  // Update breadcrumbs
  if (data.breadcrumbs) {
    updateBreadcrumbs(data.breadcrumbs);
  }

  // Add additional SEO meta tags
  updateMetaTag('property', 'og:type', 'website');
  updateMetaTag('property', 'og:site_name', 'USublease');
  updateMetaTag('property', 'og:locale', 'en_US');
  updateMetaTag('name', 'twitter:card', 'summary_large_image');
  updateMetaTag('name', 'twitter:site', '@usublease');
}

/**
 * Helper function to update or create a meta tag
 */
function updateMetaTag(attribute: 'name' | 'property', value: string, content: string): void {
  const selector = `meta[${attribute}="${value}"]`;
  let metaTag = document.querySelector(selector) as HTMLMetaElement;

  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute(attribute, value);
    document.head.appendChild(metaTag);
  }

  metaTag.content = content;
}

/**
 * Update structured data (JSON-LD)
 */
function updateStructuredData(data: object): void {
  // Remove existing structured data scripts
  const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
  existingScripts.forEach(script => script.remove());

  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-dynamic', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Update breadcrumbs structured data
 */
function updateBreadcrumbs(breadcrumbs: Array<{ name: string; url: string }>): void {
  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  };
  updateStructuredData(breadcrumbList);
}

/**
 * Generate SEO data for a college/university page
 */
export function generateCollegeSEO(
  universityName: string,
  abbreviation?: string,
  listingCount?: number,
  slug?: string
): SEOData {
  const displayName = abbreviation || universityName;
  const baseUrl = 'https://usublease.com';
  const pageSlug = slug || (abbreviation?.toLowerCase().replace(/[^a-z0-9]+/g, '-') ||
    universityName.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
  const canonical = `${baseUrl}/college/${pageSlug}`;

  const title = `USublease | usub | ${displayName} Student Subleases`;

  // Enhanced description with more natural language and keywords
  const description = listingCount
    ? `Browse ${listingCount}+ verified ${displayName} subleases and student housing listings on usub. Find affordable off-campus apartments, rooms, and sublets near ${universityName}. Connect directly with students and secure your perfect housing for Fall, Spring, or Summer terms.`
    : `Find verified ${displayName} subleases and student housing near ${universityName} on usub. Browse affordable off-campus apartments, rooms, and sublets. Connect directly with students and secure your perfect housing for Fall, Spring, or Summer terms.`;

  // Comprehensive keywords including common search variations
  const keywords = [
    'usub',
    'usublease',
    `${displayName} sublease`,
    `${displayName} sublet`,
    `${universityName} housing`,
    `${displayName} student housing`,
    `${displayName} off-campus housing`,
    `${displayName} apartments`,
    `${displayName} rooms for rent`,
    `student housing near ${universityName}`,
    `${displayName} summer housing`,
    `${displayName} summer sublet`,
    `${displayName} fall housing`,
    `${displayName} spring housing`,
    `${displayName} housing sublet`,
    `${displayName} roommates`,
    `sublease ${displayName}`,
    `sublet ${displayName}`,
    `${universityName} off campus housing`,
    `${displayName} student apartments`,
    `${displayName} housing search`,
    `find housing ${displayName}`,
    `${displayName} rental`,
    `${displayName} student rental`
  ].join(', ');

  // Structured data for the college page
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayName} Subleases`,
    description: description,
    url: canonical,
    mainEntity: {
      '@type': 'ItemList',
      name: `${displayName} Student Housing Listings`,
      description: `Verified sublease listings for ${universityName}`,
      numberOfItems: listingCount || 0,
      itemListElement: listingCount ? {
        '@type': 'ListItem',
        position: 1,
        name: `Student housing listings for ${displayName}`
      } : undefined
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: baseUrl
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: `${displayName} Subleases`,
          item: canonical
        }
      ]
    },
    // Add organization context
    publisher: {
      '@type': 'Organization',
      name: 'USublease',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://usublease.com/favicon.png'
      }
    }
  };

  const breadcrumbs = [
    { name: 'Home', url: baseUrl },
    { name: `${displayName} Subleases`, url: canonical }
  ];

  return {
    title,
    description,
    keywords,
    canonical,
    ogTitle: title,
    ogDescription: description,
    ogImage: 'https://usublease.com/favicon.png',
    ogUrl: canonical,
    twitterTitle: title,
    twitterDescription: description.substring(0, 200), // Twitter has character limits
    twitterImage: 'https://usublease.com/favicon.png',
    structuredData,
    breadcrumbs
  };
}

/**
 * Generate SEO data for a listing page
 */
export function generateListingSEO(
  listing: {
    id: string;
    title: string;
    description: string;
    monthly_rent: number;
    address: string;
    unit_type: string;
    lease_term: string;
    university_name?: string;
    university_abbreviation?: string;
    pictures?: string[];
    parking?: boolean | string;
    allow_pets?: boolean | string;
    gym?: boolean | string;
    pool?: boolean | string;
    gender_preference?: string;
    created_at?: string;
  }
): SEOData {
  const baseUrl = 'https://usublease.com';
  const listingUrl = `${baseUrl}/listing/${listing.id}`;
  const universityName = listing.university_name || listing.university_abbreviation || '';
  const universityDisplay = listing.university_abbreviation || listing.university_name || '';

  // Create rich title with key information
  const title = `${listing.title} | USublease | usub | $${listing.monthly_rent}/mo${universityDisplay ? ` | ${universityDisplay}` : ''}`;

  // Create comprehensive description
  const amenities: string[] = [];
  if (listing.parking === true || String(listing.parking).toLowerCase() === 'true') amenities.push('parking');
  if (listing.allow_pets === true || String(listing.allow_pets).toLowerCase() === 'true') amenities.push('pet-friendly');
  if (listing.gym === true || String(listing.gym).toLowerCase() === 'true') amenities.push('gym');
  if (listing.pool === true || String(listing.pool).toLowerCase() === 'true') amenities.push('pool');

  const amenitiesText = amenities.length > 0 ? ` with ${amenities.join(', ')}` : '';
  const termText = listing.lease_term ? ` for ${listing.lease_term}` : '';
  const locationText = universityName ? ` near ${universityName}` : '';

  const description = `${listing.title} - $${listing.monthly_rent}/month${termText}${locationText}. ${listing.description.substring(0, 100)}${amenitiesText ? `. Features: ${amenities.join(', ')}.` : ''} View photos, contact owner, and secure your student housing sublease.`;

  // Generate keywords
  const keywords = [
    'usub',
    'usublease',
    listing.title,
    `${listing.monthly_rent} rent`,
    `${universityDisplay} sublease`,
    `${universityDisplay} sublet`,
    `${universityDisplay} student housing`,
    `${universityDisplay} off-campus housing`,
    listing.unit_type,
    listing.lease_term,
    'student sublease',
    'student housing',
    'off-campus housing',
    'college housing',
    'university housing',
    ...amenities
  ].filter(Boolean).join(', ');

  // Helper to get absolute image URL
  const getAbsoluteImageUrl = (imageUrl: string | undefined): string => {
    if (!imageUrl) return 'https://usublease.com/favicon.png';
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    // If it's a relative path, it's likely from the API - use as is (API should serve full URLs)
    // For SEO, we want the actual accessible URL
    return imageUrl.startsWith('/') ? `https://usublease.com${imageUrl}` : imageUrl;
  };

  // Use first listing image for OG image, or fallback
  const ogImage = listing.pictures && listing.pictures.length > 0
    ? getAbsoluteImageUrl(listing.pictures[0])
    : 'https://usublease.com/favicon.png';

  // Structured data for Product/Place schema
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': ['Product', 'Place'],
    name: listing.title,
    description: listing.description,
    url: listingUrl,
    image: listing.pictures && listing.pictures.length > 0
      ? listing.pictures.map((pic: string) => getAbsoluteImageUrl(pic))
      : ['https://usublease.com/favicon.png'],
    offers: {
      '@type': 'Offer',
      price: listing.monthly_rent,
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: listing.monthly_rent,
        priceCurrency: 'USD',
        unitCode: 'MON'
      }
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: listing.address,
      addressLocality: universityName || 'University Area'
    },
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Unit Type',
        value: listing.unit_type
      },
      {
        '@type': 'PropertyValue',
        name: 'Lease Term',
        value: listing.lease_term
      },
      ...(listing.parking === true || String(listing.parking).toLowerCase() === 'true' ? [{
        '@type': 'PropertyValue',
        name: 'Parking',
        value: 'Available'
      }] : []),
      ...(listing.allow_pets === true || String(listing.allow_pets).toLowerCase() === 'true' ? [{
        '@type': 'PropertyValue',
        name: 'Pets Allowed',
        value: 'Yes'
      }] : []),
      ...(listing.gym === true || String(listing.gym).toLowerCase() === 'true' ? [{
        '@type': 'PropertyValue',
        name: 'Gym',
        value: 'Available'
      }] : []),
      ...(listing.pool === true || String(listing.pool).toLowerCase() === 'true' ? [{
        '@type': 'PropertyValue',
        name: 'Pool',
        value: 'Available'
      }] : [])
    ],
    publisher: {
      '@type': 'Organization',
      name: 'USublease',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://usublease.com/favicon.png'
      }
    },
    ...(listing.created_at ? {
      datePublished: listing.created_at
    } : {})
  };

  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Home', url: baseUrl }
  ];

  if (universityDisplay) {
    const universitySlug = (listing.university_abbreviation || listing.university_name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-');
    breadcrumbs.push({
      name: `${universityDisplay} Subleases`,
      url: `${baseUrl}/college/${universitySlug}`
    });
  }

  breadcrumbs.push({
    name: listing.title,
    url: listingUrl
  });

  return {
    title,
    description: description.substring(0, 160), // Keep within meta description limits
    keywords,
    canonical: listingUrl,
    ogTitle: title,
    ogDescription: description.substring(0, 200),
    ogImage,
    ogUrl: listingUrl,
    twitterTitle: title,
    twitterDescription: description.substring(0, 200),
    twitterImage: ogImage,
    structuredData,
    breadcrumbs
  };
}

/**
 * Generate SEO data for home page
 */
export function generateHomeSEO(totalCount?: number): SEOData {
  const baseUrl = 'https://usublease.com';
  const title = 'USublease';

  const description = totalCount && totalCount > 0
    ? `Browse ${totalCount}+ verified subleases and off-campus housing listings on usub. Find affordable housing near your university and connect directly with other users on the #1 sublease platform.`
    : 'usub is the #1 student sublease platform. Find and post subleases near your university. Browse verified listings, connect with other users, and secure your perfect off-campus housing.';

  const keywords = [
    'usub',
    'usublease',
    'student sublease',
    'student sublet',
    'off-campus housing',
    'university housing',
    'college housing',
    'student apartments',
    'student rentals',
    'campus housing',
    'student accommodation',
    'sublet',
    'sublease',
    'student housing',
    'college apartments',
    'university apartments',
    'student roommates',
    'off campus housing',
    'student housing search'
  ].join(', ');

  // Structured data for home page
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'USublease',
    url: baseUrl,
    description: 'Student sublease platform connecting students with affordable off-campus housing near universities.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    },
    publisher: {
      '@type': 'Organization',
      name: 'USublease',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://usublease.com/favicon.png',
        width: 512,
        height: 512
      }
    },
    ...(totalCount && totalCount > 0 ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.5',
        reviewCount: totalCount.toString()
      }
    } : {})
  };

  return {
    title,
    description,
    keywords,
    canonical: baseUrl,
    ogTitle: title,
    ogDescription: description,
    ogImage: 'https://usublease.com/favicon.png',
    ogUrl: baseUrl,
    twitterTitle: title,
    twitterDescription: description.substring(0, 200),
    twitterImage: 'https://usublease.com/favicon.png',
    structuredData
  };
}

/**
 * Reset SEO to default values
 */
export function resetSEO(): void {
  updateSEO({
    title: 'USublease',
    description: 'Find affordable student subleases near your university on usub. Browse verified listings, connect with students, and secure your perfect off-campus housing.',
    canonical: 'https://usublease.com/',
    ogTitle: 'USublease',
    ogDescription: 'Find affordable student subleases near your university on usub. Browse verified listings, connect with students, and secure your perfect off-campus housing.',
    ogImage: 'https://usublease.com/favicon.png',
    ogUrl: 'https://usublease.com/',
    twitterTitle: 'USublease',
    twitterDescription: 'Find affordable student subleases near your university on usub. Browse verified listings and connect with students.',
    twitterImage: 'https://usublease.com/favicon.png'
  });
}
